import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { FolderKanban } from "lucide-react";
import { Nav } from "@/components/prometheus/Nav";
import { OsShell } from "@/components/prometheus/os/OsShell";
import { OsSidebar, type OsNavId } from "@/components/prometheus/os/OsSidebar";
import { MonitorPanel } from "@/components/prometheus/os/MonitorPanel";
import { ProjectsSidebarList } from "@/components/prometheus/os/ProjectsSidebarList";
import { ProjectWorkspacePanel } from "@/components/prometheus/os/ProjectWorkspacePanel";
import { ChatsSidebarList } from "@/components/prometheus/os/ChatsSidebarList";
import { UniversidadPanel } from "@/components/prometheus/os/UniversidadPanel";
import { InventarioPanel } from "@/components/prometheus/os/InventarioPanel";
import { OsSyncPanel } from "@/components/prometheus/os/OsSyncPanel";
import {
  GENERAL_ID,
  chatIdForProject,
  touchChatEntry,
} from "@/lib/prometheusChatIndex";
import { getProject } from "@/lib/prometheusProjectsStore";

const ChatWidget = lazy(() =>
  import("@/components/prometheus/ChatWidget").then((m) => ({ default: m.ChatWidget }))
);

const PrometheusAutomationStudio = lazy(() =>
  import("@/components/prometheus/PrometheusAutomationStudio").then((m) => ({
    default: m.PrometheusAutomationStudio,
  }))
);

function ChatCanvas() {
  return (
    <div className="relative h-full w-full min-h-0">
      <Suspense
        fallback={
          <div className="flex h-full items-center justify-center font-mono-tech text-xs text-muted-foreground">
            Cargando chat…
          </div>
        }
      >
        <ChatWidget embedded />
      </Suspense>
    </div>
  );
}

function ProjectObserveEmpty() {
  return (
    <div className="flex h-full items-center justify-center p-6 pt-14 md:pt-6 bg-void">
      <div className="max-w-md text-center space-y-3">
        <FolderKanban className="w-8 h-8 text-primary mx-auto opacity-80" />
        <h2 className="font-serif-display text-xl text-foreground">Elige un proyecto</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Ábrelo desde la lista para ver despiece, wiring, pasos y bitácora. El chat no se abre aquí —
          usa «Continuar en chat» cuando quieras editar o avanzar.
        </p>
      </div>
    </div>
  );
}

export default function ChatHomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = searchParams.get("project");
  const [section, setSection] = useState<OsNavId>(() =>
    projectId ? "projects" : "chats"
  );

  useEffect(() => {
    if (projectId && section !== "chats" && section !== "projects") {
      setSection("projects");
    }
  }, [projectId, section]);

  useEffect(() => {
    const onSection = (e: Event) => {
      const detail = (e as CustomEvent<{ section?: OsNavId }>).detail;
      if (detail?.section) setSection(detail.section);
    };
    const onOpenProject = (e: Event) => {
      const detail = (e as CustomEvent<{ projectId?: string; tab?: string }>).detail;
      const id = detail?.projectId;
      if (!id) return;
      const next = new URLSearchParams(searchParams);
      next.set("project", id);
      if (detail?.tab) next.set("tab", detail.tab);
      else next.delete("tab");
      setSearchParams(next, { replace: false });
      setSection("projects");
    };
    window.addEventListener("prometheus-os-section", onSection);
    window.addEventListener("prometheus-open-project", onOpenProject);
    return () => {
      window.removeEventListener("prometheus-os-section", onSection);
      window.removeEventListener("prometheus-open-project", onOpenProject);
    };
  }, [searchParams, setSearchParams]);

  const clearProjectParam = () => {
    if (!projectId) return;
    const next = new URLSearchParams(searchParams);
    next.delete("project");
    setSearchParams(next, { replace: true });
  };

  /** Observe-only: open project content, never the chat. */
  const selectProject = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("project", id);
    setSearchParams(next, { replace: false });
    setSection("projects");
  };

  /** Explicit continue: jump to Chats with project context. */
  const openProjectChat = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("project", id);
    setSearchParams(next, { replace: false });
    setSection("chats");
    const title = getProject(id)?.title;
    touchChatEntry({ id: chatIdForProject(id), projectId: id, title });
    window.dispatchEvent(new Event("prometheus-chat-index-changed"));
    window.dispatchEvent(
      new CustomEvent("open-prometheus-chat", {
        detail: {
          projectId: id,
          characterId: "prometheus",
          autoSend: false,
        },
      })
    );
  };

  const openGeneralChat = () => {
    clearProjectParam();
    setSection("chats");
    touchChatEntry({ id: GENERAL_ID, title: "Chat general" });
    window.dispatchEvent(new Event("prometheus-chat-index-changed"));
  };

  const handleNewChat = () => {
    clearProjectParam();
    setSection("chats");
    touchChatEntry({ id: GENERAL_ID, title: "Chat general" });
    window.dispatchEvent(new Event("prometheus-chat-index-changed"));
    window.dispatchEvent(new CustomEvent("prometheus-new-chat"));
  };

  const handleSectionSelect = (id: string) => {
    const navId = id as OsNavId;
    setSection(navId);
    // Leaving projects/chats clears project deep-link except when staying in chats with context
    if (navId !== "projects" && navId !== "chats" && projectId) {
      clearProjectParam();
    }
    // Mobile drawer: close after pick so content is usable; OsShell FAB reopens anytime
    try {
      if (window.matchMedia("(max-width: 767px)").matches) {
        window.dispatchEvent(new Event("prometheus-os-close-sidebar"));
      }
    } catch {
      /* ignore */
    }
  };

  let main: ReactNode;
  if (section === "chats") {
    main = <ChatCanvas />;
  } else if (section === "projects") {
    main = projectId ? (
      <div className="h-full min-h-0 overflow-hidden bg-void">
        <ProjectWorkspacePanel projectId={projectId} className="h-full max-w-5xl mx-auto border-x border-border/40" />
      </div>
    ) : (
      <ProjectObserveEmpty />
    );
  } else if (section === "monitor") {
    main = <MonitorPanel onSelectProject={selectProject} />;
  } else if (section === "universidad") {
    main = <UniversidadPanel />;
  } else if (section === "inventario") {
    main = <InventarioPanel />;
  } else if (section === "automations") {
    main = (
      <div className="h-full min-h-0 overflow-hidden bg-void pt-12 md:pt-0">
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center font-mono-tech text-xs text-muted-foreground">
              Cargando automatizaciones…
            </div>
          }
        >
          <PrometheusAutomationStudio />
        </Suspense>
      </div>
    );
  } else {
    main = (
      <div className="h-full overflow-y-auto bg-void p-4 pt-12 sm:p-6 md:pt-6">
        <div className="max-w-lg mx-auto space-y-4">
          <header className="space-y-1">
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.18em] text-primary font-bold">
              Personalizar
            </p>
            <h2 className="font-serif-display text-xl text-foreground">Sync y preferencias</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Respaldá el Chat OS entre dispositivos. Más opciones de tema/agente llegan después.
            </p>
          </header>
          <OsSyncPanel />
        </div>
      </div>
    );
  }

  // Right Artifact peek only while chatting with a linked project (optional context)
  const panel =
    section === "chats" && projectId ? (
      <ProjectWorkspacePanel projectId={projectId} />
    ) : undefined;

  const sidebarExtra =
    section === "projects" ? (
      <ProjectsSidebarList selectedId={projectId} onSelect={selectProject} />
    ) : section === "chats" ? (
      <ChatsSidebarList
        activeProjectId={projectId}
        onOpenGeneral={openGeneralChat}
        onOpenProjectChat={openProjectChat}
      />
    ) : null;

  return (
    <div className="min-h-dvh bg-void">
      <Nav variant="os" />
      <OsShell
        sidebar={
          <OsSidebar
            active={section}
            onSelect={handleSectionSelect}
            onNewChat={handleNewChat}
            extra={sidebarExtra}
          />
        }
        main={main}
        panel={panel}
      />
    </div>
  );
}
